import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import SortableAnswer from "./SortableAnswer";
import { v4 as uuidv4 } from "uuid";

const SortableQuestion = (props) => {
  const { question, setQuestions, selectedQuestionId, setSelectedQuestionId } =
    props;
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [content, setContent] = useState(question.content);
  const [debouncedContent, setDebouncedContent] = useState(question.content);

  const [answers, setAnswers] = useState(question.answers || []);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = answers.findIndex((item) => item.id === active.id);
      const newIndex = answers.findIndex((item) => item.id === over.id);
      const array = arrayMove(answers, oldIndex, newIndex);
      setAnswers(array);
    }
  };
  useEffect(() => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) =>
        q.id === question.id ? { ...q, answers: answers } : q,
      ),
    );
  }, [answers]);

  useEffect(() => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) =>
        q.id === question.id ? { ...q, content: content } : q,
      ),
    );
  }, [debouncedContent]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedContent(content);
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [content, 200]);

  const handleNewAnswer = () => {
    const newAnswer = {
      content: "",
      id: uuidv4(),
      correct: false,
    };
    setAnswers([...answers, newAnswer]);
  };

  const handleSelectedQuestionChange = () => {
    if (selectedQuestionId && question.id === selectedQuestionId) {
      setSelectedQuestionId(null);
      return;
    }
    setSelectedQuestionId(question.id);
  };

  return (
    <div ref={setNodeRef} style={style} className="gap-2 px-4 py-1">
      <span className="text-lg cursor-move mr-4" {...attributes} {...listeners}>
        ≡
      </span>
      <input
        type="text"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        className="w-3/4 mt-1 border px-3 py-2 text-sm"
        placeholder="Wpisz tutaj treść pytania..."
      />
      <button
        className="text-primaryYellow ml-2"
        onClick={() => handleSelectedQuestionChange()}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-4"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m19.5 8.25-7.5 7.5-7.5-7.5"
          />
        </svg>
      </button>

      {selectedQuestionId && question.id == selectedQuestionId && (
        <>
          <div>
            <span className="text-sm">Odpowiedzi </span>
            <span className="text-sm text-gray-500">
              (zaznacz jedną lub kilka poprawnych odpowiedzi)
            </span>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={answers.map((answer) => answer.id)}
              strategy={verticalListSortingStrategy}
            >
              {answers.map((answer) => (
                <SortableAnswer
                  key={answer.id}
                  answer={answer}
                  setAnswers={setAnswers}
                />
              ))}
            </SortableContext>
          </DndContext>

          <div className="px-6 py-3 flex justify-between">
            <button
              className="flex items-center gap-1 text-primaryYellow"
              onClick={() => {
                handleNewAnswer();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
              Dodaj odpowiedź
            </button>

            <button
              className="flex items-center gap-1 text-primaryYellow"
              onClick={() =>
                setQuestions((prev) => prev.filter((q) => q.id !== question.id))
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                />
              </svg>
              Usuń pytanie
            </button>
          </div>
        </>
      )}
    </div>
  );
};

SortableQuestion.propTypes = {
  id: PropTypes.string.isRequired,
  selectedQuestionId: PropTypes.string.isRequired,
  question: PropTypes.shape({
    id: PropTypes.number,
    content: PropTypes.string,
    answers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        content: PropTypes.string,
        correct: PropTypes.bool,
      }),
    ),
  }),
  setQuestions: PropTypes.func.isRequired,
  setSelectedQuestionId: PropTypes.func.isRequired,
};

export default SortableQuestion;
